import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTransferTagsMutation } from "@/data/mutations";
import { useGetSKUsForSKUSelectionQuery } from "@/data/queries";
import { useDialogState } from "@/hooks/use-dialog-state";

type Props = {
  skuId: string;
  tagCount: number;
};

const moveTagsFormSchema = z.object({
  toTemplateId: z.string().nonempty(),
  startingSerial: z.string().nonempty(),
  endingSerial: z.string().nonempty(),
});

export const MoveTagsDialog: FC<Props> = ({ skuId, tagCount }) => {
  const form = useForm({
    resolver: zodResolver(moveTagsFormSchema),
    defaultValues: {
      toTemplateId: "",
      startingSerial: "",
      endingSerial: "",
    },
  });
  const transferTagsMutation = useTransferTagsMutation(skuId);
  const { dialog, closeDialog } = useDialogState();
  const { data } = useGetSKUsForSKUSelectionQuery();

  const handleSubmit = (values: z.infer<typeof moveTagsFormSchema>) => {
    transferTagsMutation.mutate({
      fromTemplateId: skuId,
      toTemplateId: values.toTemplateId,
      startingSerial: Number(values.startingSerial),
      endingSerial: Number(values.endingSerial),
    });
  };

  if (!data) {
    return null;
  }

  return (
    <AlertDialog open={dialog?.name === "move-tags"}>
      <AlertDialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <AlertDialogHeader>
              <AlertDialogTitle>Move Tags</AlertDialogTitle>
              <AlertDialogDescription>
                Transfer a range of tags by serial number to a different SKU.
                The serial number of the moved tags will be reordered to match
                the new SKU's sequence.
              </AlertDialogDescription>

              <FormField
                control={form.control}
                name="toTemplateId"
                render={({ field }) => (
                  <FormItem className="mt-2">
                    <FormLabel>Receiving SKU*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select SKU" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {data.templates.map((sku) => (
                          <SelectItem
                            key={sku.id}
                            value={sku.id.toString()}
                            disabled={skuId === sku.id}
                          >
                            {sku.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startingSerial"
                render={({ field }) => (
                  <FormItem className="mt-2">
                    <FormLabel>Starting Serial Number</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} placeholder="1" {...field} />
                    </FormControl>
                    <FormDescription>
                      This is the first tag in the range of tags to transfer,
                      inclusive of this tag.
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endingSerial"
                render={({ field }) => (
                  <FormItem className="mt-2">
                    <FormLabel>Ending Serial Number</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder={`${tagCount}`}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      This is the last tag in the range of tags to transfer,
                      inclusive of this tag.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </AlertDialogHeader>

            <AlertDialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={closeDialog}
                disabled={transferTagsMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={transferTagsMutation.isPending}>
                {transferTagsMutation.isPending ? (
                  <Loader className="animate-spin" />
                ) : null}
                Move
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
